import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignRecipientDto } from '@carbonwebmail/contracts'
import { SelectField } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import { EmptyCard, PagedTable } from '../../ui/PagedScreen.js'
import type { PillTone } from '../../ui/StatusPill.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Td, Tr } from '../../ui/Table.js'
import type { RecipientFilter } from './useCampaignRecipients.js'
import { useCampaignRecipients } from './useCampaignRecipients.js'

/**
 * Who the campaign went to, one person per row.
 *
 * The figures above are the summary; this is the answer to the question they
 * provoke — "which eleven?" Without it, the only way to find out who a send
 * missed is to ask somebody with database access, which is how a marketing
 * person ends up unable to act on their own campaign's report.
 *
 * The filters are exactly the states in the Queue panel above, so choosing one
 * shows the rows behind a number somebody has just read.
 *
 * Two state columns rather than one verdict. **Where it stands** is the queue
 * now; **first answer** is what the provider said the first time the message
 * was attempted, and the send log keeps only that first answer (I3) — so a
 * message that failed once and then went out reads `failed` in one column and
 * `done` in the other. Both are true, and merging them would mean choosing
 * which to hide.
 */

/** The same words the Queue panel above uses, so one is visibly the other's rows. */
const FILTERS: readonly (readonly [string, string])[] = [
  ['all', 'Everyone'],
  ['queued', 'Waiting'],
  ['claimed', 'In flight'],
  ['done', 'Done'],
  ['unknown', 'Unknown'],
  ['dead', 'Dead'],
]

/**
 * Keyed by the contract's own unions, so a state added to either fails the
 * build here until somebody decides what it should look like.
 */
const QUEUE: Record<CampaignRecipientDto['queue'], { label: string; tone: PillTone }> = {
  queued: { label: 'waiting', tone: 'neutral' },
  claimed: { label: 'in flight', tone: 'neutral' },
  done: { label: 'done', tone: 'ok' },
  // Never the danger tone. The provider did not answer, so this may well have
  // arrived — that is the state this product exists to show rather than hide. (I7)
  unknown: { label: 'unknown', tone: 'unknown' },
  dead: { label: 'dead', tone: 'danger' },
}

const OUTCOME: Record<NonNullable<CampaignRecipientDto['outcome']>, PillTone> = {
  accepted: 'ok',
  duplicate: 'neutral',
  rejected: 'danger',
  failed: 'warn',
  unknown: 'unknown',
}

export function CampaignRecipients({
  client,
  campaignId,
}: {
  readonly client: ApiClient
  readonly campaignId: string
}) {
  const { filter, narrow, state, setPage, retry } = useCampaignRecipients({ client, campaignId })

  return (
    <section aria-label="Recipients" className="mt-6">
      <h2 className="font-bold text-[15px] text-t1">Recipients</h2>
      <p className="mt-1 text-[13px] text-t2">
        One row per person. <strong className="font-semibold">Where it stands</strong> is the queue
        now; <strong className="font-semibold">first answer</strong> is what the provider said the
        first time this message was tried, which is kept even if a later attempt went out.
      </p>

      <div className="mt-3 mb-3 max-w-xs">
        <SelectField
          id="recipient-state"
          label="Show"
          value={filter}
          /* Never disabled: changing the filter mid-load starts the right request. */
          disabled={false}
          options={FILTERS}
          onChange={(value) => narrow(value as RecipientFilter)}
        />
      </div>

      <PagedTable
        state={state}
        onRetry={() => void retry()}
        onPage={setPage}
        head={['Recipient', 'Where it stands', 'First answer', 'Reason', 'Answered']}
        minWidth={840}
        empty={
          <EmptyCard heading="Nobody here">
            {filter === 'all'
              ? 'This campaign has no queue rows at all, which means it was never queued.'
              : 'No recipient is in that state. Choose “Everyone” to see the whole list.'}
          </EmptyCard>
        }
        row={(recipient) => <Row key={recipient.email} recipient={recipient} />}
      />
    </section>
  )
}

function Row({ recipient }: { readonly recipient: CampaignRecipientDto }) {
  const queue = QUEUE[recipient.queue]

  return (
    <Tr>
      <Td strong>
        {recipient.email}
        {recipient.name === '' ? null : (
          <span className="ml-2 font-normal text-[12px] text-t3">{recipient.name}</span>
        )}
      </Td>
      <Td>
        <StatusPill tone={queue.tone}>{queue.label}</StatusPill>
      </Td>
      <Td>
        {recipient.outcome === null ? (
          // Not "no answer". Nothing was sent, so nothing was answered, and an
          // empty pill would read as the provider having stayed silent.
          <span className="text-t3">not tried yet</span>
        ) : (
          <StatusPill tone={OUTCOME[recipient.outcome]}>{recipient.outcome}</StatusPill>
        )}
      </Td>
      {/* The provider's own sentence. "Rejected" with no reason sends somebody
          to the database to find out why. */}
      <Td>{recipient.reason ?? '—'}</Td>
      <Td>{recipient.answeredAt === null ? '—' : when(recipient.answeredAt)}</Td>
    </Tr>
  )
}
